import { shouldShowForCart } from 'helpers/cart-conditions.mjs'

/**
 * Shows or hides its children based on the cart's item count, so merchants can build separate
 * empty-cart and filled-cart content out of ordinary theme blocks.
 *
 * Liquid renders the correct initial state from `cart.item_count` and marks the wrapper `hidden`,
 * so there is no flash before this runs. From then on the count arrives on `updateCartCount`,
 * which helpers/cart.js dispatches after every `getCart()`.
 *
 * Basecamp's version listens to three events behind a 300ms debounce plus a separate sync event
 * carrying the count. That is not needed here: `updateCartCount` already carries `detail.count`
 * and fires on every cart refresh, so one listener is both simpler and free of the debounce race.
 */
class ConditionalCartContentElement extends HTMLElement {
  static EVENT_UPDATE_CART_COUNT = 'updateCartCount'

  connectedCallback() {
    this.handleCartCount = this.handleCartCount.bind(this)
    document.addEventListener(ConditionalCartContentElement.EVENT_UPDATE_CART_COUNT, this.handleCartCount)

    /** The cart singleton may already have fetched before this element upgraded. */
    const known = window.Cart?.value?.item_count
    if (typeof known === 'number') this.applyCount(known)
  }

  disconnectedCallback() {
    document.removeEventListener(ConditionalCartContentElement.EVENT_UPDATE_CART_COUNT, this.handleCartCount)
  }

  handleCartCount(event) {
    this.applyCount(event?.detail?.count)
  }

  applyCount(itemCount) {
    const visible = shouldShowForCart(this.dataset.cartCondition, this.dataset.cartCount, itemCount)
    this.hidden = !visible
    /** Liquid ships an inline `display:none`, which `hidden` alone cannot beat. */
    this.style.display = visible ? '' : 'none'
  }
}

customElements.define('conditional-cart-content', ConditionalCartContentElement)
